export const dataProcessingContent = `# ACUERDO DE PROCESAMIENTO DE DATOS (DPA)

## Data Processing Agreement — PricingIntelligence

---

> **AVISO IMPORTANTE:** Este documento es un modelo referencial y requiere revision obligatoria por un abogado habilitado en el Estado Plurinacional de Bolivia antes de su uso. No constituye asesoramiento juridico. Los placeholders indicados entre corchetes [ASI] deben ser completados con la información correspondiente antes de la suscripción del acuerdo.

---

## PARTES

**EL CONTROLADOR DE DATOS:**

[NOMBRE_EMPRESA_CLIENTE] (en adelante, "el Controlador" o "el Cliente"), sociedad [TIPO_SOCIEDAD_CLIENTE] con NIT [NIT_CLIENTE], con domicilio en [DIRECCION_CLIENTE], [CIUDAD_CLIENTE], Estado Plurinacional de Bolivia.

**EL PROCESADOR DE DATOS:**

[NOMBRE DE LA EMPRESA] (en adelante, "el Procesador" o "PI"), sociedad [TIPO_SOCIEDAD] con NIT [NIT], con domicilio en [DIRECCION], [CIUDAD], Estado Plurinacional de Bolivia.

---

## CLAUSULA PRIMERA — DEFINICIONES

**1.1.** A los efectos del presente DPA:

   a) **"Datos Personales":** Toda informacion relacionada con una persona natural identificada o identificable;

   b) **"Controlador de Datos":** La persona que determina los fines y medios del tratamiento de datos personales;

   c) **"Procesador de Datos":** La persona que trata datos personales por cuenta del Controlador;

   d) **"Subprocesador":** Todo tercero contratado por el Procesador para llevar a cabo actividades de tratamiento;

   e) **"Tratamiento":** Cualquier operacion realizada sobre datos personales;

   f) **"Brecha de Datos Personales":** Cualquier incidente de seguridad que resulte en la destruccion, perdida, alteracion, divulgacion o acceso no autorizado a datos personales.

---

## CLAUSULA SEGUNDA — OBJETO Y ALCANCE

**2.1.** El presente DPA establece las obligaciones y responsabilidades de las Partes en relacion con el tratamiento de datos personales que el Procesador realiza por cuenta del Controlador en el marco de la prestacion del Servicio PricingIntelligence.

**2.2.** El presente DPA se aplica a todos los datos personales tratados por el Procesador en nombre del Controlador a traves de la Plataforma.

**2.3.** En caso de conflicto entre el DPA y el Contrato Principal respecto al tratamiento de datos personales, prevalecera el DPA.

---

## CLAUSULA TERCERA — DATOS PROCESADOS Y FINALIDADES

### 3.1. Datos de empleados y usuarios del Controlador

| Categoria de Datos | Ejemplos | Finalidad |
|---|---|---|
| Datos de identificacion | Nombre completo, correo electronico corporativo | Creacion y gestion de cuentas de usuario |
| Datos de autenticacion | Contrasena cifrada, tokens de sesion | Autenticacion y control de acceso |
| Datos de rol y permisos | Rol asignado, permisos de acceso | Gestion de autorizaciones |
| Datos de actividad | Logs de acceso, acciones realizadas | Auditoria, seguridad y soporte tecnico |

### 3.2. Datos de clientes del Controlador

| Categoria de Datos | Ejemplos | Finalidad |
|---|---|---|
| Datos de contacto comercial | Nombre del contacto, empresa, telefono, email | Generacion de cotizaciones |
| Datos fiscales | Razon social, NIT, domicilio fiscal | Datos fiscales del destinatario |

### 3.3. Datos incluidos en consultas al Asistente de IA

| Categoria de Datos | Ejemplos | Finalidad |
|---|---|---|
| Texto de consultas | Preguntas formuladas por el usuario | Generacion de respuestas |
| Datos contextuales | Nombres de productos, precios | Contextualizacion de las respuestas |

---

## CLAUSULA CUARTA — OBLIGACIONES DEL PROCESADOR

**4.1.** El Procesador se obliga a:

   a) Tratar los datos personales unicamente conforme a las instrucciones documentadas del Controlador;
   b) Garantizar que el personal autorizado este sujeto a obligaciones de confidencialidad;
   c) Implementar y mantener las medidas tecnicas y organizativas de seguridad;
   d) No subcontratar sin cumplir con lo establecido en la clausula quinta;
   e) Asistir al Controlador en el cumplimiento de derechos de los Titulares de Datos;
   f) Asistir al Controlador en la gestion de brechas de datos personales;
   g) Eliminar o devolver todos los datos personales una vez finalizada la prestacion del Servicio;
   h) Poner a disposicion del Controlador la informacion necesaria para demostrar el cumplimiento;
   i) Permitir y colaborar con las auditorias.

---

## CLAUSULA QUINTA — SUBPROCESADORES

**5.1.** Subprocesadores autorizados a la fecha de suscripcion:

| Subprocesador | Ubicacion | Actividad de Tratamiento | Datos Procesados |
|---|---|---|---|
| **Anthropic, Inc.** | Estados Unidos | Procesamiento de consultas al Asistente de IA | Texto de consultas y datos contextuales |
| **[PROVEEDOR_HOSTING]** | [PAIS_HOSTING] | Alojamiento de infraestructura | Todos los datos almacenados |
| **[PROVEEDOR_BASE_DATOS]** | [PAIS_BD] | Servicio de base de datos gestionada | Todos los datos estructurados |
| **[PROVEEDOR_EMAIL]** | [PAIS_EMAIL] | Envio de correos transaccionales | Direcciones de correo, nombre, contenido |

**5.2.** El Procesador notificara al Controlador con al menos treinta (30) dias de anticipacion antes de incorporar un nuevo Subprocesador.

---

## CLAUSULA SEXTA — MEDIDAS DE SEGURIDAD

### 6.1. Medidas Tecnicas

   a) **Cifrado en transito:** TLS 1.3;
   b) **Cifrado en reposo:** AES-256;
   c) **Cifrado de credenciales:** Funciones hash con sal (bcrypt);
   d) **Aislamiento de datos (Multi-Tenant):** Separacion logica estricta por Tenant;
   e) **Autenticacion segura:** JWT con tokens de corta duracion;
   f) **Control de acceso basado en roles (RBAC):** Cuatro niveles de acceso;
   g) **Copias de seguridad:** Respaldos automatizados diarios con retencion de 30 dias;
   h) **Monitoreo y logs:** Registro de actividad de acceso y operaciones;
   i) **Firewall y proteccion perimetral:** Proteccion contra ataques comunes.

### 6.2. Medidas Organizativas

   a) Acceso restringido al minimo necesario;
   b) Acuerdos de confidencialidad con todo el personal;
   c) Capacitacion periodica sobre proteccion de datos;
   d) Procedimientos documentados de gestion de incidentes;
   e) Pruebas trimestrales de restauracion de copias de seguridad.

---

## CLAUSULA SEPTIMA — NOTIFICACION DE BRECHAS

**7.1.** En caso de brecha de datos personales, el Procesador notificara al Controlador dentro de las setenta y dos (72) horas siguientes a la deteccion, proporcionando:

   - Descripcion de la naturaleza de la brecha;
   - Nombre y datos de contacto del responsable;
   - Descripcion de las consecuencias probables;
   - Descripción de las medidas adoptadas o propuestas.

**7.2.** Un informe detallado sera proporcionado dentro de los siete (7) dias siguientes.

---

## CLAUSULA OCTAVA — AUDITORIAS

**8.1.** El Controlador podra realizar una (1) auditoria por ano calendario, con notificacion previa de treinta (30) dias.

**8.2.** El Procesador podra satisfacer los requisitos mediante informes de auditoria de terceros (SOC 2, ISO 27001 u otros equivalentes).

---

## CLAUSULA NOVENA — TRANSFERENCIAS INTERNACIONALES

**9.1.** El Controlador autoriza la transferencia de datos a los Subprocesadores internacionales detallados en la clausula quinta.

**9.2.** Todas las transferencias se realizan a traves de canales cifrados (TLS 1.3 o superior).

**9.3. Transferencia a Anthropic, Inc.:** Solo se transfieren datos incluidos en la consulta del usuario. Anthropic no utiliza datos de la API para entrenar sus modelos.

---

## CLAUSULA DECIMA — ELIMINACION DE DATOS POST-CONTRATO

**10.1.** A la terminacion del Contrato Principal:

| Sistema | Plazo de Eliminacion |
|---|---|
| Sistemas de produccion | 30 dias |
| Copias de seguridad | 90 dias |
| Logs y registros | 180 dias |

**10.2.** El Procesador certificara por escrito la eliminacion efectiva de los datos.

---

## CLAUSULA DECIMOPRIMERA — OBLIGACIONES DEL CONTROLADOR

**11.1.** El Controlador se obliga a proporcionar instrucciones licitas, garantizar la base legal adecuada, obtener consentimientos necesarios e informar a los Titulares de Datos sobre el tratamiento.

---

## CLAUSULA DECIMOSEGUNDA — RESPONSABILIDAD

**12.1.** Cada Parte sera responsable de los danos causados por el tratamiento contrario a las obligaciones del presente DPA.

**12.2.** La responsabilidad del Procesador se regira por las limitaciones establecidas en el Contrato Principal.

---

## CLAUSULA DECIMOTERCERA — VIGENCIA

**13.1.** El presente DPA permanecera vigente durante toda la vigencia del Contrato Principal.

**13.2.** Las obligaciones que por su naturaleza deban subsistir (eliminacion de datos, confidencialidad, responsabilidad) continuaran en vigor hasta su cumplimiento integro.

---

## CLAUSULA DECIMOCUARTA — DISPOSICIONES FINALES

**14.1.** El presente DPA complementa y forma parte integrante del Contrato Principal.

**14.2.** Cualquier modificacion debera constar por escrito y ser firmada por representantes autorizados de ambas Partes.

**14.3.** Si alguna disposicion fuera declarada invalida, las restantes conservaran plena vigencia.

---

## APENDICE 1 — DESCRIPCION DEL TRATAMIENTO

| Aspecto | Detalle |
|---|---|
| **Objeto del tratamiento** | Prestacion del servicio PricingIntelligence |
| **Duracion del tratamiento** | Durante la vigencia del Contrato Principal |
| **Naturaleza del tratamiento** | Almacenamiento, procesamiento, consulta, generacion de reportes, procesamiento por IA |
| **Finalidad del tratamiento** | Gestion de precios, cotizaciones, materiales, flujos de aprobacion, asistencia por IA |
| **Categorias de datos personales** | Datos de identificacion, contacto, laborales, comerciales, de uso |
| **Categorias de Titulares** | Empleados del Controlador, clientes del Controlador |
| **Datos personales sensibles** | No se tratan datos sensibles de forma sistematica |

---

**FIN DEL ACUERDO DE PROCESAMIENTO DE DATOS**

**PricingIntelligence**
**Version del DPA: 1.0**
`;
